import { useEffect, useRef, type ReactNode } from "react";

/**
 * Detail beside the list, rather than a page you scroll back from.
 *
 * Opening an event meant leaving the radar, scrolling to find what you
 * came for, and going back to where you were — which is three actions to
 * answer one question. Drata puts a control's detail in a panel for the
 * same reason: the list is the context, and losing it to read one row is
 * the wrong trade.
 *
 * It closes on Escape and on a click outside, keeps the tab key inside
 * while it is open, and gives focus back to whatever had it when it
 * closes — so somebody working the list with j/k lands back on the row
 * they were on rather than at the top of the document.
 */
export function Drawer({ open, onClose, title, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    panel.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab") return;
      // Keep tab inside the panel: a drawer you can tab out of behind is a
      // reader operating controls they cannot see.
      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      returnTo.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="drawer__scrim" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}
        tabIndex={-1} ref={panel}>
        <header className="drawer__head">
          <h2 className="drawer__title">{title}</h2>
          <button className="drawer__close" onClick={onClose} aria-label="Close">
            esc
          </button>
        </header>
        <div className="drawer__body">{children}</div>
      </aside>
    </div>
  );
}
